import { useId, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Download, Filter, NotebookPen, Mail, RefreshCw, UserRound, X } from 'lucide-react';
import { Botao, Cartao } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { BuscaRemota, CampoData, Rotulo, Selecao } from '../../componentes/formulario';
import { anunciarOcultos } from '../../componentes/sensivel';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { get, getTodos } from '../../nucleo/api';
import { data, dinheiro, nomePessoa, numero } from '../../nucleo/formato';
import { rotuloEtapaPipeline, rotuloFunilPipeline, rotuloMotivoPerda } from '../../nucleo/rotulos';
import { useSessao } from '../../nucleo/sessao';
import { baixarTexto, Estrelas } from './CrmComponentes';
import { csv } from './crm-sugestoes';
import { funilDoPedido, lerPedidoDoRelatorioCrm, mesAtual, tipoDoRelatorioCrm } from './crm-caminhos';
import { useListas } from './crm-listas';
import { useFunis } from './Funis';
import { iconeDoTipo } from './icones-atividade';
import { RelatorioTipos, semFunil, semResponsavel, TIPOS_RELATORIO, type TipoRelatorio } from './RelatorioTipos';

/**
 * Lote crm5 (E4, ficha 2026-10-28 "crm-empresas-relatorio-e-card"): o relatório do CRM, o "Dashboard" da origem (destino do
 * "Ver relatório completo" das Atividades). Funil e responsável na barra; Data, Campanha, Fonte e Produto ou serviço no
 * drawer "Filtros" (aplicados só no "Aplicar filtros"). Os números vêm agregados do servidor (`/pipeline/crm/relatorio`);
 * cada bloco exporta o próprio CSV a partir desses agregados. Valores pelos formatadores (modo discreto).
 */

export interface ValoresMoeda {
  moeda: string;
  unidades: number;
  total_centavos: number;
  unico_centavos: number;
  recorrente_centavos: number;
}
export interface RelatorioCrm {
  funil_id: string;
  resumo: {
    criadas: number;
    vendas: number;
    perdidas: number;
    sem_proxima_tarefa: number;
    sem_interacoes: number;
    recem_alocadas: number;
  };
  vendas: ValoresMoeda[];
  tarefas: {
    tipo: string;
    criadas: number;
    pendentes: number;
    finalizadas: number;
  }[];
  atualizacoes: {
    anotacoes: number;
    emails: number;
    estrelas: { estrelas: number; n: number }[];
  };
  entradas: { etapa_id: string; nome: string; n: number }[];
  perdidas: {
    valores: ValoresMoeda[];
    motivos: { motivo_id: string | null; nome: string | null; n: number }[];
  };
}

export interface FiltrosRelatorio {
  de: string;
  ate: string;
  campanha: string;
  fonte: string;
  servico_id: string;
  servico_nome: string;
}

type TipoValor = 'total' | 'unico' | 'recorrente';
const CAMPO_VALOR: Record<TipoValor, keyof ValoresMoeda> = {
  total: 'total_centavos',
  unico: 'unico_centavos',
  recorrente: 'recorrente_centavos',
};
const RESUMO = ['criadas', 'vendas', 'perdidas', 'sem_proxima_tarefa', 'sem_interacoes', 'recem_alocadas'] as const;
const COR_RESUMO: Partial<Record<(typeof RESUMO)[number], string>> = {
  criadas: 'text-ciano',
  vendas: 'text-verde',
  perdidas: 'text-perigo',
};
const COLUNAS = ['criadas', 'pendentes', 'finalizadas'] as const;

/** Valor por moeda (nunca somando moedas): cada moeda no formatador, separadas por " · "; sem linhas, zero na moeda padrão. */
export function valorPorMoeda(linhas: ValoresMoeda[], f: (l: ValoresMoeda) => number): string {
  if (!linhas.length) return dinheiro(0);
  return linhas.map((l) => dinheiro(f(l), l.moeda)).join(' · ');
}

/** Quantos filtros do drawer estão ligados (a data sempre conta, como na origem). */
export const contarFiltrosRelatorio = (f: FiltrosRelatorio) => 1 + (f.campanha ? 1 : 0) + (f.fonte ? 1 : 0) + (f.servico_id ? 1 : 0);

export function PaginaRelatorioCrm() {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const busca = useLocation().search;
  // O pedido do endereço vale na chegada (o "Ver relatório completo"); depois, a barra e o drawer mandam.
  const [pedido] = useState(() => lerPedidoDoRelatorioCrm(busca));
  // Lote crm8 (item 6): o "Tipo de relatório" da origem; o "Ver relatório completo" (com período) chega no Dashboard.
  const [tipo, setTipo] = useState<TipoRelatorio>(() => tipoDoRelatorioCrm(busca));
  const funis = useFunis();
  const ativos = (funis.data?.itens ?? []).filter((f) => !f.arquivado_em);
  const [funilId, setFunilId] = useState(pedido.funil);
  const funil = funilDoPedido(funilId, ativos);
  const [responsavel, setResponsavel] = useState(pedido.responsavel);
  const [f, setF] = useState<FiltrosRelatorio>({
    de: pedido.de,
    ate: pedido.ate,
    campanha: '',
    fonte: '',
    servico_id: '',
    servico_nome: '',
  });
  const [drawer, setDrawer] = useState(false);
  const [tipoValor, setTipoValor] = useState<TipoValor>('total');
  const membros = useQuery({
    queryKey: ['membros', 'todos'],
    queryFn: () => getTodos<{ id: string; nome: string }>('/membros'),
    staleTime: 300_000,
  });
  const params = useMemo(
    () => ({
      funil_id: funil,
      de: f.de,
      ate: f.ate,
      ...(responsavel ? { responsaveis: responsavel } : {}),
      ...(f.campanha ? { campanha: f.campanha } : {}),
      ...(f.fonte ? { fonte: f.fonte } : {}),
      ...(f.servico_id ? { servico_id: f.servico_id } : {}),
    }),
    [funil, responsavel, f],
  );
  const rel = useQuery({
    queryKey: ['pipeline', 'crm', 'relatorio', params],
    queryFn: () => get<RelatorioCrm>('/pipeline/crm/relatorio', params),
    enabled: !!funil && tipo === 'dashboard',
    placeholderData: keepPreviousData,
  });
  const r = rel.data;
  // Os outros tipos: o mesmo recorte sem o período (Equipes e Metas sem o responsável, como na origem).
  const paramsTipos = useMemo(() => {
    const { de: _de, ate: _ate, responsaveis, ...resto } = params as Record<string, string | undefined>;
    void _de;
    void _ate;
    // Rodada rlote-4 (SPEC-B2): em Ligações e no Comparativo o seletor de funil some (como na origem): sem `funil_id`, todos.
    const ligacoes = tipo === 'ligacoes' || tipo === 'comparativo';
    const { funil_id: funilId, ...semFunil } = resto;
    return { ...(ligacoes ? { ...semFunil, todos_funis: 'true' } : { ...semFunil, ...(funilId ? { funil_id: funilId } : {}) }), ...(responsaveis && !semResponsavel(tipo) ? { responsaveis } : {}) } as Record<string, string>;
  }, [params, tipo]);

  const ehLigacao = tipo === 'ligacoes' || tipo === 'comparativo';
  const outros = (membros.data?.itens ?? []).filter((m) => m.id !== s.usuario.id);
  const opcoesResp = [{ valor: s.usuario.id, rotulo: t('crm.relatorio.minhas') }, ...outros.map((m) => ({ valor: m.id, rotulo: nomePessoa(m.nome) }))];
  // Lote crm8: o período vale só no Dashboard (os outros tipos da origem não o usam).
  const etiquetas: { chave: string; texto: string; tirar?: () => void }[] =
    tipo === 'dashboard'
      ? [
          {
            chave: 'data',
            texto: `${t('crm.relatorio.data')}: ${t('crm.relatorio.periodo', { de: data(f.de), ate: data(f.ate) })}`,
          },
        ]
      : [];
  if (f.campanha)
    etiquetas.push({
      chave: 'campanha',
      texto: `${t('crm.relatorio.campanha')}: ${f.campanha}`,
      tirar: () => setF({ ...f, campanha: '' }),
    });
  if (f.fonte)
    etiquetas.push({
      chave: 'fonte',
      texto: `${t('crm.relatorio.fonte')}: ${f.fonte}`,
      tirar: () => setF({ ...f, fonte: '' }),
    });
  if (f.servico_id)
    etiquetas.push({
      chave: 'produto',
      texto: `${t('crm.relatorio.produto')}: ${f.servico_nome}`,
      tirar: () => setF({ ...f, servico_id: '', servico_nome: '' }),
    });

  const exportar = (chave: keyof typeof BLOCOS_CSV) => {
    if (!r) return;
    baixarTexto(t('crm.relatorio.arquivo', { bloco: chave }), csv(BLOCOS_CSV[chave](r, t, tipoValor)));
    toast.sucesso(t('crm.relatorio.exportado'));
  };
  // R3-SEG-M1: sem `pipeline.exportar` o botão não existe (a permissão vale só na tela).
  const baixar = (chave: keyof typeof BLOCOS_CSV) =>
    !s.pode('pipeline.exportar') ? null : (
    <button
      type="button"
      className="rounded-lg p-2 text-tinta-3 hover:bg-sup-3 hover:text-tinta-1 disabled:opacity-40"
      aria-label={t('crm.relatorio.exportar', {
        bloco: t(`crm.relatorio.blocos.${chave}`),
      })}
      title={t('crm.relatorio.exportar', {
        bloco: t(`crm.relatorio.blocos.${chave}`),
      })}
      disabled={!r}
      data-exportar={chave}
      onClick={() => exportar(chave)}
    >
      <Download className="size-4" aria-hidden />
    </button>
    );

  return (
    <div className="flex min-w-0 flex-col gap-4" data-pagina-relatorio-crm="">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="kicker">{t('casca.relatorios')}</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">{t('crm.relatorio.titulo')}</h1>
          {tipo === 'dashboard' && <p className="mt-1 text-sm text-tinta-3">{t('crm.relatorio.subtitulo')}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Botao
            icone={<RefreshCw className="size-4" />}
            data-recarregar=""
            onClick={async () => {
              await qc.invalidateQueries({
                queryKey: ['pipeline', 'crm', 'relatorio'],
              });
              toast.sucesso(t('crm.relatorio.recarregado'));
            }}
          >
            {t('crm.relatorio.recarregar')}
          </Botao>
          <Botao icone={<Filter className="size-4" />} onClick={() => setDrawer(true)} data-abrir-filtros="">
            {t('crm.relatorio.filtros', { n: contarFiltrosRelatorio(f) })}
          </Botao>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2" data-filtros-relatorio="">
        <div className="w-full sm:w-56">
          <Selecao
            aria-label={t('crm.relatorio.tipos.rotulo')}
            data-tipo-relatorio=""
            value={tipo}
            opcoes={TIPOS_RELATORIO.map((x) => ({ valor: x, rotulo: t(`crm.relatorio.tipos.nomes.${x}`) }))}
            onChange={(e) => setTipo(e.target.value as TipoRelatorio)}
          />
        </div>
        {!semFunil(tipo) && (
        <div className="w-full sm:w-56">
          <Selecao
            aria-label={t('crm.relatorio.funil')}
            icone={<Filter className="size-4" />}
            data-funil=""
            value={funil}
            opcoes={ativos.map((x) => ({
              valor: x.id,
              rotulo: rotuloFunilPipeline(x.nome),
            }))}
            onChange={(e) => setFunilId(e.target.value)}
          />
        </div>
        )}
        {!semResponsavel(tipo) && (
        <div className="w-full sm:w-60">
          <Selecao
            aria-label={t('crm.relatorio.responsavel')}
            icone={<UserRound className="size-4" />}
            data-responsavel=""
            value={responsavel}
            vazio={t(ehLigacao ? 'crm.relatorio.tipos.todasLigacoes' : 'crm.relatorio.todas')}
            opcoes={ehLigacao ? opcoesResp.map((o, i) => (i ? o : { ...o, rotulo: t('crm.relatorio.tipos.minhasLigacoes') })) : opcoesResp}
            onChange={(e) => setResponsavel(e.target.value)}
          />
        </div>
        )}
      </div>

      <ul className="flex flex-wrap items-center gap-2" aria-label={t('crm.relatorio.filtrosLigados')} data-etiquetas="">
        {etiquetas.map((x) => (
          <li key={x.chave} className={`flex items-center gap-1 rounded-full border border-borda bg-sup-1 py-0.5 text-xs ${x.tirar ? 'pr-1 pl-3' : 'px-3 py-1'}`} data-etiqueta={x.chave}>
            {x.texto}
            {x.tirar && (
              <button type="button" className="rounded-full p-1 hover:bg-sup-3" aria-label={t('crm.relatorio.tirar', { nome: x.texto })} onClick={x.tirar}>
                <X className="size-3" aria-hidden />
              </button>
            )}
          </li>
        ))}
      </ul>

      {tipo !== 'dashboard' ? (
        <RelatorioTipos tipo={tipo} params={paramsTipos} moeda={s.workspace.moeda} />
      ) : rel.isError && !r ? (
        <Erro erro={rel.error} aoTentar={() => void rel.refetch()} />
      ) : !r ? (
        <Carregando texto={t('crm.relatorio.carregando')} />
      ) : (
        <div className="flex min-w-0 flex-col gap-4" aria-busy={rel.isFetching} data-relatorio="">
          <Cartao titulo={t('crm.relatorio.blocos.resumo')} acao={baixar('resumo')}>
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6" data-bloco="resumo">
              {RESUMO.map((k) => (
                <Numero key={k} rotulo={t(`crm.relatorio.resumo.${k}`)} cor={COR_RESUMO[k]} dado={k}>
                  {numero(r.resumo[k])}
                </Numero>
              ))}
            </dl>
          </Cartao>

          <Cartao
            titulo={t('crm.relatorio.blocos.valorVolume')}
            acao={
              <div className="flex items-center gap-1">
                <div className="w-44">
                  <Selecao
                    aria-label={t('crm.relatorio.valorTipo')}
                    data-tipo-valor=""
                    value={tipoValor}
                    opcoes={(['total', 'unico', 'recorrente'] as const).map((v) => ({
                      valor: v,
                      rotulo: t(`crm.relatorio.valores.${v}`),
                    }))}
                    onChange={(e) => setTipoValor(e.target.value as TipoValor)}
                  />
                </div>
                {baixar('valorVolume')}
              </div>
            }
          >
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-bloco="valorVolume">
              <Numero rotulo={t('crm.relatorio.valor')} cor="text-verde" dado="valor">
                {valorPorMoeda(r.vendas, (l) => Number(l[CAMPO_VALOR[tipoValor]]))}
              </Numero>
              <Numero rotulo={t('crm.relatorio.unidades')} dado="unidades">
                {numero(r.vendas.reduce((a, l) => a + l.unidades, 0))}
              </Numero>
              <Numero rotulo={t('crm.relatorio.ticket')} dado="ticket">
                {valorPorMoeda(r.vendas, (l) => (l.unidades ? Math.round(Number(l[CAMPO_VALOR[tipoValor]]) / l.unidades) : 0))}
              </Numero>
            </dl>
          </Cartao>

          <Cartao titulo={t('crm.relatorio.blocos.tarefas')} acao={baixar('tarefas')}>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3" data-bloco="tarefas">
              {COLUNAS.map((c) => (
                <div key={c} className="min-w-0" data-coluna={c}>
                  <h3 className="mb-2 text-center text-sm font-semibold text-tinta-2">{t(`crm.relatorio.colunasTarefas.${c}`)}</h3>
                  {/* Lista de 30/10 (item 17): a lista fica estreita e centrada sob o título da coluna, com o número perto do tipo
                      (na largura toda o número ia para a outra ponta da coluna). */}
                  <ul className="mx-auto flex w-full max-w-60 flex-col" data-lista-tarefas="">
                    {r.tarefas.map((x) => {
                      const Icone = iconeDoTipo(x.tipo);
                      return <Linha key={x.tipo} icone={<Icone className="size-4" aria-hidden />} rotulo={t(`tipoAtividade.${x.tipo}`)} valor={numero(x[c])} />;
                    })}
                    <Linha total rotulo={t('crm.relatorio.total')} valor={numero(r.tarefas.reduce((a, x) => a + x[c], 0))} />
                  </ul>
                </div>
              ))}
            </div>
          </Cartao>

          {/* RCRM5 VIS-M1: 3 cartões, 3 colunas (a 4ª ficava vazia e cada cartão com 1/4 da largura). */}
          <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-3" data-linha-baixo-relatorio="">
            <Cartao titulo={t('crm.relatorio.blocos.atualizacoes')} acao={baixar('atualizacoes')}>
              <ul className="flex flex-col" data-bloco="atualizacoes">
                <Linha icone={<NotebookPen className="size-4" aria-hidden />} rotulo={t('crm.relatorio.anotacoes')} valor={numero(r.atualizacoes.anotacoes)} />
                <Linha icone={<Mail className="size-4" aria-hidden />} rotulo={t('crm.relatorio.emails')} valor={numero(r.atualizacoes.emails)} />
                {r.atualizacoes.estrelas.map((x) => (
                  <Linha
                    key={x.estrelas}
                    icone={
                      <Estrelas
                        valor={x.estrelas}
                        rotulo={t('crm.relatorio.estrelas', {
                          count: x.estrelas,
                        })}
                      />
                    }
                    valor={numero(x.n)}
                  />
                ))}
              </ul>
            </Cartao>
            <Cartao titulo={t('crm.relatorio.blocos.entradas')} acao={baixar('entradas')}>
              {r.entradas.length ? (
                <ul className="flex flex-col" data-bloco="entradas">
                  {r.entradas.map((x) => (
                    <Linha key={x.etapa_id} rotulo={rotuloEtapaPipeline(x.nome)} valor={numero(x.n)} />
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-tinta-3">{t('crm.relatorio.semEtapas')}</p>
              )}
            </Cartao>
            <Cartao titulo={<span className="text-perigo">{t('crm.relatorio.blocos.perdidas')}</span>} acao={baixar('perdidas')}>
              {/* A divisão em duas colunas responde à largura do cartão (container), não à da janela: só quando ele é largo. */}
              <div className="@container min-w-0">
                <div className="grid grid-cols-1 gap-4 @lg:grid-cols-2" data-bloco="perdidas">
                  <div className="min-w-0">
                    <h3 className="mb-2 text-sm font-semibold text-tinta-2">{t('crm.relatorio.valoresPerdidos')}</h3>
                    <ul className="flex flex-col">
                      <Linha rotulo={t('crm.relatorio.perdidoUnico')} valor={valorPorMoeda(r.perdidas.valores, (l) => l.unico_centavos)} />
                      <Linha rotulo={t('crm.relatorio.perdidoRecorrente')} valor={valorPorMoeda(r.perdidas.valores, (l) => l.recorrente_centavos)} />
                      <Linha rotulo={t('crm.relatorio.perdidoTotal')} valor={valorPorMoeda(r.perdidas.valores, (l) => l.total_centavos)} />
                    </ul>
                  </div>
                  <div className="min-w-0">
                    <h3 className="mb-2 text-sm font-semibold text-tinta-2">{t('crm.relatorio.motivos')}</h3>
                    {r.perdidas.motivos.length ? (
                      <ul className="flex flex-col">
                        {r.perdidas.motivos.map((x) => (
                          <Linha key={x.motivo_id ?? 'sem'} rotulo={x.nome ? rotuloMotivoPerda(x.nome) : t('crm.relatorio.semMotivo')} valor={numero(x.n)} />
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm font-medium text-tinta-2" data-sem-perdidas="">
                        {t('crm.relatorio.semPerdidas')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </Cartao>
          </div>
        </div>
      )}

      {drawer && (
        <DrawerFiltrosRelatorio
          atual={f}
          aoFechar={() => setDrawer(false)}
          aoAplicar={(novo) => {
            setF(novo);
            setDrawer(false);
          }}
        />
      )}
    </div>
  );
}

function Numero({ rotulo, cor, dado, children }: { rotulo: string; cor?: string; dado: string; children: ReactNode }) {
  return (
    <div className="min-w-0 rounded-lg bg-sup-2 px-3 py-2">
      <dt className={`text-xs font-semibold tracking-wide uppercase ${cor ?? 'text-tinta-3'}`}>{rotulo}</dt>
      <dd className="numero mt-1 text-2xl font-bold break-words text-tinta-1" data-numero={dado}>
        {anunciarOcultos(children)}
      </dd>
    </div>
  );
}

function Linha({ icone, rotulo, valor, total }: { icone?: ReactNode; rotulo?: string; valor: string; total?: boolean }) {
  return (
    <li className={`flex min-h-9 items-center gap-3 text-sm text-tinta-2 ${total ? 'mt-1 border-t border-borda pt-1 font-semibold text-tinta-1' : ''}`}>
      {icone && <span className="flex shrink-0 text-tinta-3">{icone}</span>}
      <span className="min-w-0 flex-1 break-words">{rotulo}</span>
      <span className="numero shrink-0 font-medium text-tinta-1">{anunciarOcultos(valor)}</span>
    </li>
  );
}

type Traduzir = (chave: string, o?: Record<string, unknown>) => string;
/** As linhas de cada CSV, só com os agregados do servidor (valores pelos formatadores: o modo discreto vale no arquivo). */
export const BLOCOS_CSV = {
  resumo: (r: RelatorioCrm, t: Traduzir) => [[t('crm.relatorio.item'), t('crm.relatorio.quantidade')], ...RESUMO.map((k) => [t(`crm.relatorio.resumo.${k}`), r.resumo[k]])],
  valorVolume: (r: RelatorioCrm, t: Traduzir, tipo: TipoValor) => [
    [t('crm.relatorio.item'), t(`crm.relatorio.valores.${tipo}`)],
    [t('crm.relatorio.valor'), valorPorMoeda(r.vendas, (l) => Number(l[CAMPO_VALOR[tipo]]))],
    [t('crm.relatorio.unidades'), r.vendas.reduce((a, l) => a + l.unidades, 0)],
    [t('crm.relatorio.ticket'), valorPorMoeda(r.vendas, (l) => (l.unidades ? Math.round(Number(l[CAMPO_VALOR[tipo]]) / l.unidades) : 0))],
  ],
  tarefas: (r: RelatorioCrm, t: Traduzir) => [
    [t('crm.relatorio.coluna'), t('crm.relatorio.tipo'), t('crm.relatorio.quantidade')],
    ...COLUNAS.flatMap((c) => [
      ...r.tarefas.map((x) => [t(`crm.relatorio.colunasTarefas.${c}`), t(`tipoAtividade.${x.tipo}`), x[c]]),
      [t(`crm.relatorio.colunasTarefas.${c}`), t('crm.relatorio.total'), r.tarefas.reduce((a, x) => a + x[c], 0)],
    ]),
  ],
  atualizacoes: (r: RelatorioCrm, t: Traduzir) => [
    [t('crm.relatorio.item'), t('crm.relatorio.quantidade')],
    [t('crm.relatorio.anotacoes'), r.atualizacoes.anotacoes],
    [t('crm.relatorio.emails'), r.atualizacoes.emails],
    ...r.atualizacoes.estrelas.map((x) => [t('crm.relatorio.estrelas', { count: x.estrelas }), x.n]),
  ],
  entradas: (r: RelatorioCrm, t: Traduzir) => [[t('crm.relatorio.etapa'), t('crm.relatorio.quantidade')], ...r.entradas.map((x) => [rotuloEtapaPipeline(x.nome), x.n])],
  perdidas: (r: RelatorioCrm, t: Traduzir) => [
    [t('crm.relatorio.item'), t('crm.relatorio.quantidade')],
    [t('crm.relatorio.perdidoUnico'), valorPorMoeda(r.perdidas.valores, (l) => l.unico_centavos)],
    [t('crm.relatorio.perdidoRecorrente'), valorPorMoeda(r.perdidas.valores, (l) => l.recorrente_centavos)],
    [t('crm.relatorio.perdidoTotal'), valorPorMoeda(r.perdidas.valores, (l) => l.total_centavos)],
    ...r.perdidas.motivos.map((x) => [`${t('crm.relatorio.motivos')}: ${x.nome ? rotuloMotivoPerda(x.nome) : t('crm.relatorio.semMotivo')}`, x.n]),
  ],
} satisfies Record<string, (r: RelatorioCrm, t: Traduzir, tipo: TipoValor) => (string | number)[][]>;

/** O drawer "Filtros" da origem: Data, Campanha, Fonte e Produto ou serviço; vale só no "Aplicar filtros". */
function DrawerFiltrosRelatorio({ atual, aoAplicar, aoFechar }: { atual: FiltrosRelatorio; aoAplicar: (f: FiltrosRelatorio) => void; aoFechar: () => void }) {
  const { t } = useTranslation('pipeline');
  const listas = useListas();
  const id = useId();
  const [f, setF] = useState(atual);
  const mudar = (d: Partial<FiltrosRelatorio>) => setF((x) => ({ ...x, ...d }));
  const invalido = !f.de || !f.ate || f.de > f.ate;
  const opcoes = (xs: { nome: string }[] | undefined, valor: string) => {
    const nomes = (xs ?? []).map((x) => x.nome);
    // O valor em uso fica na lista mesmo que tenha saído das Configurações.
    return [...(valor && !nomes.includes(valor) ? [valor] : []), ...nomes].map((n) => ({ valor: n, rotulo: n }));
  };
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      titulo={t('crm.relatorio.filtrosTitulo')}
      rodape={
        <>
          <Botao
            variante="fantasma"
            data-limpar-filtros=""
            onClick={() =>
              aoAplicar({
                ...mesAtual(),
                campanha: '',
                fonte: '',
                servico_id: '',
                servico_nome: '',
              })
            }
          >
            {t('crm.relatorio.limpar')}
          </Botao>
          <Botao onClick={aoFechar}>{t('crm.relatorio.cancelar')}</Botao>
          <Botao variante="primario" data-aplicar-filtros="" disabled={invalido} onClick={() => aoAplicar(f)}>
            {t('crm.relatorio.aplicar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4" data-drawer-filtros-relatorio="">
        <p className="rounded-lg bg-sup-2 px-3 py-2 text-sm text-tinta-2">
          {t('crm.relatorio.filtrosAtivos', {
            count: contarFiltrosRelatorio(f),
          })}
        </p>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1.5 text-sm font-medium text-tinta-2">{t('crm.relatorio.data')}</legend>
          <div className="grid grid-cols-2 gap-3">
            <Rotulo id={`${id}-de`} rotulo={t('crm.relatorio.de')}>
              <CampoData type="date" id={`${id}-de`} data-de="" value={f.de} onChange={(e) => mudar({ de: e.target.value })} />
            </Rotulo>
            <Rotulo id={`${id}-ate`} rotulo={t('crm.relatorio.ate')} erro={f.de && f.ate && f.de > f.ate ? t('crm.relatorio.periodoInvalido') : undefined}>
              <CampoData type="date" id={`${id}-ate`} data-ate="" value={f.ate} onChange={(e) => mudar({ ate: e.target.value })} />
            </Rotulo>
          </div>
        </fieldset>
        <Rotulo id={`${id}-campanha`} rotulo={t('crm.relatorio.campanha')}>
          <Selecao
            id={`${id}-campanha`}
            data-campanha=""
            value={f.campanha}
            vazio={t('crm.relatorio.todasOpcoes')}
            opcoes={opcoes(listas.data?.campanhas, f.campanha)}
            onChange={(e) => mudar({ campanha: e.target.value })}
          />
        </Rotulo>
        <Rotulo id={`${id}-fonte`} rotulo={t('crm.relatorio.fonte')}>
          <Selecao
            id={`${id}-fonte`}
            data-fonte=""
            value={f.fonte}
            vazio={t('crm.relatorio.todasOpcoes')}
            opcoes={opcoes(listas.data?.fontes, f.fonte)}
            onChange={(e) => mudar({ fonte: e.target.value })}
          />
        </Rotulo>
        <Rotulo id={`${id}-produto`} rotulo={t('crm.relatorio.produto')}>
          <BuscaRemota
            id={`${id}-produto`}
            def={{
              caminho: '/cadastros/servicos',
              rotulo: (i) => String(i.nome),
            }}
            valor={f.servico_id || null}
            rotuloAtual={f.servico_nome || undefined}
            placeholder={t('crm.relatorio.todosProdutos')}
            aoMudar={(v, item) =>
              mudar({
                servico_id: v ?? '',
                servico_nome: item ? String(item.nome) : '',
              })
            }
          />
        </Rotulo>
      </div>
    </Drawer>
  );
}
